import { useCallback, useState } from "react";
import { toast } from "@jsq/ds/core";
import { useAppContext } from "~/contexts/appContext";
import {
  ApproverSlot as ApproverSlotEnum,
  FamilyChangeRequestDecision as FamilyChangeRequestDecisionEnum,
} from "~/graphql/supergraphEnums";
import {
  useClaimGpReviewerAssignmentMutation,
  useGetGpChangeRequestQuery,
  useRecordGpApproverDecisionMutation,
  useReleaseGpReviewerAssignmentMutation,
} from "~/graphql/supergraphHooks";
import { ReviewerActionFooter } from "../ReviewerActionFooter";
import { ReviewerConclusion } from "../types";
import {
  GP_PRIMARY_CONCLUSION_OPTIONS,
  GP_SECONDARY_CONCLUSION_OPTIONS,
  UNASSIGNED_APPROVER_LABEL,
} from "./constants";
import {
  areBothLegsPending,
  getApproverSlot,
  getDecisionForSlot,
  getDecisionInputForConclusion,
  getDisplaySlot,
  getEligibleClaimSlot,
  getSlotHolderId,
  isCaseTerminal,
  isGpConclusionValue,
} from "./utils";
import type { GpConclusionValue } from "./constants";
import type { GpCheckerFooterProps } from "./types";

export const GpCheckerFooter = ({
  changeRequestId,
  onStatusChange,
  onConclusionSubmit,
}: GpCheckerFooterProps) => {
  const { user } = useAppContext();
  const userId = user?.id ?? null;

  const [conclusion, setConclusion] = useState<string>("");
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);

  const { data, refetch } = useGetGpChangeRequestQuery({
    variables: { changeRequestId },
    fetchPolicy: "cache-and-network",
  });

  const [recordDecision, { loading: recordLoading }] = useRecordGpApproverDecisionMutation({
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const [claimAssignment, { loading: claimLoading }] = useClaimGpReviewerAssignmentMutation({
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const [releaseAssignment, { loading: releaseLoading }] =
    useReleaseGpReviewerAssignmentMutation({
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const gpChangeRequest = data?.getGpChangeRequest ?? null;

  const recordConclusion = useCallback(
    async (conclusionValue: GpConclusionValue, reason?: string) => {
      const decisionInput = getDecisionInputForConclusion(conclusionValue);
      const result = await recordDecision({
        variables: {
          input: {
            changeRequestId,
            decision: decisionInput.decision,
            ...(decisionInput.resolution ? { resolution: decisionInput.resolution } : {}),
            ...(reason ? { reason } : {}),
          },
        },
      });
      const response = result.data?.recordGpApproverDecision;
      if (!response) {
        return;
      }
      setConclusion("");
      if (response.caseAutoClosed) {
        onConclusionSubmit?.(
          decisionInput.decision === FamilyChangeRequestDecisionEnum.Approved
            ? ReviewerConclusion.Approved
            : ReviewerConclusion.Invalid,
        );
        onStatusChange("CLOSED");
        return;
      }
      await refetch();
    },
    [recordDecision, changeRequestId, onConclusionSubmit, onStatusChange, refetch],
  );

  const handleSubmit = useCallback(() => {
    if (!isGpConclusionValue(conclusion)) {
      return;
    }
    if (getDecisionInputForConclusion(conclusion).requiresReason) {
      setIsReasonModalOpen(true);
      return;
    }
    void recordConclusion(conclusion);
  }, [conclusion, recordConclusion]);

  const handleReasonSubmit = useCallback(
    (reason: string) => {
      if (isGpConclusionValue(conclusion)) {
        void recordConclusion(conclusion, reason);
      }
      setIsReasonModalOpen(false);
    },
    [conclusion, recordConclusion],
  );

  const handleClaimAssignment = useCallback(async () => {
    const result = await claimAssignment({ variables: { changeRequestId } });
    if (result.data?.claimGpReviewerAssignment) {
      toast.success("Assigned to you successfully");
      await refetch();
    }
  }, [claimAssignment, changeRequestId, refetch]);

  const handleCancelAssignment = useCallback(async () => {
    // No `slot` arg — the backend releases the caller's own leg.
    const result = await releaseAssignment({ variables: { changeRequestId } });
    if (result.data?.releaseGpReviewerAssignment) {
      toast.success("Assignment cancelled successfully");
      setConclusion("");
      setIsReasonModalOpen(false);
      await refetch();
    }
  }, [releaseAssignment, changeRequestId, refetch]);

  if (!gpChangeRequest) {
    return null;
  }

  const currentSlot = getApproverSlot(userId, gpChangeRequest);
  const myDecision = currentSlot ? getDecisionForSlot(gpChangeRequest, currentSlot) : null;
  const terminal = isCaseTerminal(gpChangeRequest);
  const isLoading = recordLoading || claimLoading || releaseLoading;
  const hasPendingReview =
    currentSlot !== null && myDecision === FamilyChangeRequestDecisionEnum.Pending;

  // Claim: the caller's role-eligible slot is vacant. Cancel: the caller holds a
  // slot with a still-pending decision (mirrors the decision controls — once the
  // leg is decided, cancelling is no longer offered). Both require a non-terminal
  // case (the backend rejects otherwise).
  const eligibleClaimSlot = getEligibleClaimSlot(user?.roles);
  const showClaimAssignment =
    eligibleClaimSlot !== null &&
    getSlotHolderId(gpChangeRequest, eligibleClaimSlot) === null &&
    !terminal;
  const showCancelAssignment = hasPendingReview && !terminal;

  const displaySlot = getDisplaySlot(gpChangeRequest);
  const displayApproverName = areBothLegsPending(gpChangeRequest)
    ? [gpChangeRequest.billingApproverName, gpChangeRequest.csmManagerAssigneeName]
        .filter(Boolean)
        .join(" and ") || null
    : displaySlot === ApproverSlotEnum.Primary
      ? gpChangeRequest.billingApproverName
      : gpChangeRequest.csmManagerAssigneeName;
  const showDecisionControls = hasPendingReview && !terminal;
  // Null slot defaults to the primary list for type-satisfaction only — the
  // dropdown is unreachable then (showDecisionControls needs a non-null slot).
  const conclusionOptions =
    currentSlot === ApproverSlotEnum.Secondary
      ? GP_SECONDARY_CONCLUSION_OPTIONS
      : GP_PRIMARY_CONCLUSION_OPTIONS;

  return (
    <ReviewerActionFooter
      assigneeName={displayApproverName ?? UNASSIGNED_APPROVER_LABEL}
      showCancelAssignment={showCancelAssignment}
      onCancelAssignment={handleCancelAssignment}
      showClaimAssignment={showClaimAssignment}
      onClaimAssignment={handleClaimAssignment}
      showDecisionControls={showDecisionControls}
      conclusion={conclusion}
      onConclusionChange={setConclusion}
      conclusionOptions={conclusionOptions}
      selectPlaceholder="Select resolution"
      selectTestId="gp-resolution-select"
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isReasonModalOpen={isReasonModalOpen}
      reasonModalTitle="Invalid reason"
      reasonModalPlaceholder="State the reason for marking invalid"
      onReasonModalClose={() => {
        setIsReasonModalOpen(false);
        setConclusion("");
      }}
      onReasonModalSubmit={handleReasonSubmit}
    />
  );
};
